import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem("trade_token"));
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem("trade_token"));

  // Verify stored token on mount — if invalid, log out
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/trade/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => {
        localStorage.removeItem("trade_token");
        setToken(null);
        setIsAuthenticated(false);
      });
  }, []);

  // Global 401 interceptor — any API call that returns 401 forces logout
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (resp) => resp,
      (err) => {
        if (err.response?.status === 401 && err.config?.url?.includes("/trade/")) {
          localStorage.removeItem("trade_token");
          setToken(null);
          setIsAuthenticated(false);
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/trade/login`, { username, password });
      if (response.data.success) {
        const newToken = response.data.token;
        localStorage.setItem("trade_token", newToken);
        setToken(newToken);
        setIsAuthenticated(true);
        return { success: true };
      }
      return { success: false, error: "Login failed" };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || "Login failed" };
    }
  };

  const logout = () => {
    localStorage.removeItem("trade_token");
    setToken(null);
    setIsAuthenticated(false);
  };

  const authHeader = () => ({ Authorization: `Bearer ${token}` });

  return (
    <AuthContext.Provider value={{ token, isAuthenticated, login, logout, authHeader, API }}>
      {children}
    </AuthContext.Provider>
  );
};
